import {
  useContext,
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { deleteWorklogById, getWorklogs } from '../../utils/API/worklog';
import { getReport } from '../../utils/API/reports';
import ConfirmForm from '../confirmForm';
import { UserContext } from '../../context/UserContext';
import { MonthNavHeader } from '../../components/Layout/MonthNavHeader';
import I3Modal from '../../components/Layout/I3Modal';
import I3ModalConfirmation from '../../components/Layout/I3ModalConfirmation';
import WorklogTable from './WorklogTable';
import WorklogForm from './WorklogForm';
import PanelTotal from '../../components/PanelTotal/panelTotal';
import { getCalendarWorklogsHeader } from '../../utils/API/calendar';
import {
  extractSummaryData,
  renderSpinner,
  prepareDaysColumnHeader,
} from '../../utils/Utility';
import { MonthContext } from '../../context/MonthContext';
import { Unauthorized } from '../../components/Unauthorized';
import { useKeyPress } from '../../hooks/useKeyPress';
import { fetchAbsences } from '../../utils/API/absences';
import AbsencesTable from '../Absences/AbsencesTable';
import { EmployeeListContext } from '../../context/EmployeeListContext';
import { MonoSelectUser } from '../../components/Form/MonoSelectUser';
import { PERMISSIONS, DEFAULT_USER_WORKTIME } from '../../constants';
import { getAllIssues, getUserIssues } from '../../utils/API/issues';
import { getAllProjects, getUserProjects } from '../../utils/API/projects';
import LunchesTable from '../Lunches/LunchesTable';
import { fetchLunches } from '../../utils/API/lunches';

export const WorklogPage = () => {
  const { user: userByContext, hasClaim } = useContext(UserContext);
  const { employeesQueryA: users } = useContext(EmployeeListContext);
  const { month } = useContext(MonthContext);

  const [isLoading, setIsLoading] = useState(true);
  const [dayHeaders, setDayHeaders] = useState([]);
  const [worklogs, setWorklogs] = useState([]);
  const [user, setUser] = useState(userByContext);
  const [userIssues, setUserIssues] = useState([]);
  const [allIssues, setAllIssues] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showModalDeleteConfirmOptions, setShowModalDeleteConfirmOptions] =
    useState({
      show: false,
      user,
    });
  const [isDeleteRunning, setIsDeleteRunning] = useState(false);
  const [report, setReport] = useState({});
  const [summary, setSummary] = useState({});
  const [showModalConfirm, setShowModalConfirm] = useState(false);
  const [editFormData, setEditFormData] = useState();
  const isMountedRef = useRef(true);

  const handleToggleModal = () => setShowModal(!showModal);
  const handleShowModalConfirm = () => setShowModalConfirm(true);
  const handleHideModalConfirm = () => setShowModalConfirm(false);

  const [absences, setAbsences] = useState([]);
  const [lunches, setLunches] = useState([]);
  const [worklogRefreshTrigger, setWorklogRefreshTrigger] = useState(0);

  useEffect(() => {
    if (!user.id && userByContext.id) {
      setUser(userByContext);
    }
  }, [userByContext, user.id]);

  const canEditTempo = hasClaim(PERMISSIONS.TEMPO_EDIT);
  const isReadOnly = useMemo(
    () => report.confirmed || !report.id || userByContext.id !== user.id || !canEditTempo,
    [report, userByContext.id, user.id, canEditTempo]
  );

  const handleHideDeleteConfirmModal = () => {
    setShowModalDeleteConfirmOptions({
      show: false,
    });
  };

  const handleDeleteConfirmModal = () => {
    if (isMountedRef.current) {
      setIsDeleteRunning(true);
    }
    const { id } = showModalDeleteConfirmOptions;

    deleteWorklogById({ id })
      .then(() => {
        if (!isMountedRef.current) {
          return;
        }
        handleHideDeleteConfirmModal();
        loadData();
        setWorklogRefreshTrigger((prev) => prev + 1);
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsDeleteRunning(false);
        }
      });
  };

  const loadData = () => {
    if (isMountedRef.current) {
      setIsLoading(true);
    }
    getReport(month.format('YYYY'), month.format('MM'), user.username)
      .then((reports) => {
        const expenseReport = reports.find((i) => i.type === 'expenses');
        if (isMountedRef.current) {
          setReport(expenseReport);
        }
        const summaryData = extractSummaryData(reports);
        if (isMountedRef.current) {
          setSummary(summaryData);
        }
      })
      .then(() => getCalendarWorklogsHeader(month))
      .then((calendarWorklogsHeader) => {
        const res = prepareDaysColumnHeader({
          month,
          calendarWorklogsHeader,
        });
        if (isMountedRef.current) {
          setDayHeaders(res);
        }
      })
      .then(() =>
        fetchAbsences(month.format('YYYY'), month.format('MM'), [user.username])
      )
      .then((res) => {
        if (isMountedRef.current) {
          setAbsences(res);
        }
      })
      .then(() => getWorklogs(month, user.username))
      .then((res) => {
        if (isMountedRef.current) {
          setWorklogs(res);
        }
      })
      .then(() =>
        fetchLunches(month.format('YYYY'), month.format('MM'), user.username)
      )
      .then((res) => {
        if (isMountedRef.current) {
          setLunches(res);
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });
  };

  useEffect(loadData, [month, user]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    const getData = async () => {
      const localProjects = await getUserProjects();
      const projects = await getAllProjects();
      const localUserIssues = await getUserIssues();
      const issues = await getAllIssues();

      localProjects.sort((a, b) => a.summary.localeCompare(b.summary));
      projects.sort((a, b) => a.summary.localeCompare(b.summary));
      localUserIssues.sort((a, b) => a.summary.localeCompare(b.summary));
      issues.sort((a, b) => a.summary.localeCompare(b.summary));

      setUserProjects(localProjects);
      setAllProjects(projects);
      setUserIssues(localUserIssues);
      setAllIssues(issues);
    };

    getData().catch(console.error);
  }, []);

  const onInsert = useCallback(() => {
    if (isReadOnly) {
      return;
    }
    setEditFormData({
      issue: '',
      description: '',
      date: '',
      time_spent: userByContext.worktime
        ? userByContext.worktime
        : DEFAULT_USER_WORKTIME,
      id: null,
      report: null,
    });
    handleToggleModal();
  }, [handleToggleModal, isReadOnly, userByContext.worktime]);

  const userChange = (userId) => {
    const selectedUser = users.find((i) => i.id === userId);
    setUser(selectedUser !== undefined ? selectedUser : userByContext);
  };

  useKeyPress('F9', onInsert);
  useKeyPress('Escape', () => {
    setShowModal(false);
    setShowModalConfirm(false);
  });

  const handleDeleteClick = (worklogId) => {
    setShowModalDeleteConfirmOptions({
      ...showModalDeleteConfirmOptions,
      show: true,
      id: worklogId,
    });
  };

  const handleEditClick = (worklog) => {
    setEditFormData(worklog);
    setShowModal(true);
  };

  const worklogTableRef = useRef(null);
  const absencesTableRef = useRef(null);
  const lunchesTableRef = useRef(null);

  const onWorklogTableScroll = () => {
    absencesTableRef.current.scrollLeft = worklogTableRef.current.scrollLeft;
    lunchesTableRef.current.scrollLeft = worklogTableRef.current.scrollLeft;
  };

  const onAbsencesTableScroll = () => {
    worklogTableRef.current.scrollLeft = absencesTableRef.current.scrollLeft;
    lunchesTableRef.current.scrollLeft = absencesTableRef.current.scrollLeft;
  };

  const onLunchesTableScroll = () => {
    worklogTableRef.current.scrollLeft = lunchesTableRef.current.scrollLeft;
    absencesTableRef.current.scrollLeft = lunchesTableRef.current.scrollLeft;
  };

  if (
    !hasClaim(PERMISSIONS.TEMPO_VIEW) &&
    !hasClaim(PERMISSIONS.TEMPO_USER_SELECTION)
  ) {
    return <Unauthorized />;
  }

  return (
    <>
      <div className="content__wrapper">
        <MonthNavHeader
          title="Log Ore"
          showInsertButton={!isReadOnly}
          onInsert={onInsert}
        >
          {hasClaim(PERMISSIONS.TEMPO_USER_SELECTION) && (
            <MonoSelectUser users={users} select={userChange} />
          )}
        </MonthNavHeader>
        <div className="content__overflow">
          {isLoading ? (
            renderSpinner(true)
          ) : (
            <>
              <div
                className="tabsContainer"
                style={{ maxHeight: '90%', overflowY: 'auto' }}
              >
                <WorklogTable
                  ref={worklogTableRef}
                  onScroll={onWorklogTableScroll}
                  allIssues={allIssues}
                  userProjects={allProjects}
                  worklogs={worklogs}
                  dayHeaders={dayHeaders}
                  isReadOnly={isReadOnly}
                  username={user.username}
                  onDeleteClick={handleDeleteClick}
                  onEditClick={handleEditClick}
                  onUpdate={setSummary}
                  onWorklogChange={() =>
                    setWorklogRefreshTrigger((prev) => prev + 1)
                  }
                />
              </div>

              <div
                className="tabsContainer"
                style={{ marginTop: '0px', overflow: 'hidden' }}
              >
                <AbsencesTable
                  className="embedded-absence-table"
                  ref={absencesTableRef}
                  onScroll={onAbsencesTableScroll}
                  dayHeaders={dayHeaders}
                  data={absences}
                  isEmbeddedInWorklog={true}
                  isMonthClosed={isReadOnly}
                />
              </div>

              <div
                className="tabsContainer"
                style={{ marginTop: '0px', overflow: 'hidden' }}
              >
                <LunchesTable
                  className="embedded-lunch-table"
                  ref={lunchesTableRef}
                  onScroll={onLunchesTableScroll}
                  dayHeaders={dayHeaders}
                  data={lunches}
                  isReadOnly={isReadOnly}
                />
              </div>
            </>
          )}
        </div>
      </div>
      <PanelTotal
        report={report}
        summaryData={summary}
        handleShowModalConfirm={handleShowModalConfirm}
        disabled={isReadOnly}
        canClose={canEditTempo}
        refreshTrigger={worklogRefreshTrigger}
        user={user}
      />
      <I3Modal
        show={showModalConfirm}
        onHide={handleHideModalConfirm}
        title="Chiudi mese"
      >
        <ConfirmForm
          close={handleHideModalConfirm}
          month={month}
          reloadReport={loadData}
        />
      </I3Modal>

      <I3Modal
        show={showModal}
        onHide={handleToggleModal}
        title="Log ore"
        className="I3Modal-log-time"
      >
        <WorklogForm
          month={month}
          userIssues={userIssues}
          setUserIssues={setUserIssues}
          userProjects={userProjects}
          close={handleToggleModal}
          editFormData={editFormData}
          onDataChanged={() => {
            loadData();
            setWorklogRefreshTrigger((prev) => prev + 1);
          }}
          report={report}
        />
      </I3Modal>

      <I3ModalConfirmation
        show={showModalDeleteConfirmOptions.show}
        onHide={handleHideDeleteConfirmModal}
        title="Elimina"
        questionText="Vuoi cancellare il record selezionato?"
        onConfirm={handleDeleteConfirmModal}
        isLoading={isDeleteRunning}
      />
    </>
  );
};
